import { useEffect } from 'react';

import { LikeDislikeButtons } from '@lib/core/src/components/LikeDislikeButtons';

import { useAnalytics } from '@/analytics/useAnalytics';
import { CalendarMeetingDto, MeetingTemplateDto } from '@/api/generated';
import MeetingAnalyzeSkeleton from '@/components/Meeting/components/MeetingAnalyzeSkeleton';
import MeetingBrief from '@/components/Meeting/components/MeetingBrief';
import MeetingOverviewTasks from '@/components/Meeting/components/MeetingOverview/MeetingOverviewTasks';
import MeetingTopics from '@/components/Meeting/components/MeetingTopics';
import { useMeetingAgentsTemplate } from '@/hooks/useMeetingAgentsTemplates';

import FailedToGenerate from './FailedToGenerate';
import MeetingOverviewSections from './MeetingOverviewSections';
import { Container, TemplateSelectorContainer } from './MeetingOverviewStyles';
import TemplateSelector from './TemplateSelector';

const MeetingOverview = ({
  meeting,
  onTimeClicked,
  defaultTemplate
}: {
  meeting?: CalendarMeetingDto;
  defaultTemplate: MeetingTemplateDto;
  onTimeClicked: (time: number | undefined) => void;
}) => {
  const { captureEvent } = useAnalytics();
  const {
    meetingAnalysis,
    onTemplateClick,
    selectedTemplate,
    sections,
    isAnalyzing,
    isDefaultTemplate,
    isLoadingMeetingAnalysis,
    userAgentActiveTemplate,
    isAnalysisFailed
  } = useMeetingAgentsTemplate({ meeting, defaultTemplate });

  useEffect(() => {
    if (!isAnalyzing) return;
  }, [isAnalyzing]);

  if (isAnalyzing) {
    return <MeetingAnalyzeSkeleton name={selectedTemplate?.name} />;
  }

  const handleLike = () => {
    captureEvent('summary_like', {
      meeting_id: meeting?.id,
      template_id: selectedTemplate?.id
    });
  };

  const handleDislike = () => {
    captureEvent('summary_dislike', {
      meeting_id: meeting?.id,
      template_id: selectedTemplate?.id
    });
  };

  if (isAnalysisFailed) {
    return (
      <FailedToGenerate
        title="Summary generation failed"
        description="We've failed to generate your summary, please try again"
        onRetryClicked={() => selectedTemplate && onTemplateClick(selectedTemplate)}
      />
    );
  }

  return (
    <Container>
      <TemplateSelectorContainer>
        <LikeDislikeButtons likeCallback={handleLike} dislikeCallback={handleDislike} />
        <TemplateSelector
          selectedTemplate={selectedTemplate}
          userAgentActiveTemplate={userAgentActiveTemplate}
          onTemplateClick={onTemplateClick}
          isAnalyzing={isAnalyzing}
        />
      </TemplateSelectorContainer>
      {isDefaultTemplate && (
        <MeetingBrief brief={meeting?.overview?.brief || ''} meeting={meeting} />
      )}
      <MeetingOverviewSections
        meetingAnalysis={meetingAnalysis}
        meeting={meeting}
        sections={sections || []}
        isDefaultTemplate={isDefaultTemplate}
        isLoadingMeetingAnalysis={isLoadingMeetingAnalysis}
        onRetryClicked={() => selectedTemplate && onTemplateClick(selectedTemplate)}
      />
      {meeting?.tasks?.participantsTasks && <MeetingOverviewTasks meeting={meeting} includeTitle />}

      <MeetingTopics meeting={meeting} onTimeClicked={onTimeClicked} />
    </Container>
  );
};

export default MeetingOverview;
